// src/pages/admin/ViewPackages.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getPackages, getAllTelegramPackagesAdmin } from "../../services/api";

export default function ViewPackages() {
  // General packages (simplified)
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesPage, setPackagesPage] = useState(0);
  const [packagesRowsPerPage, setPackagesRowsPerPage] = useState(10);

  // Telegram packages (now simplified like normal packages)
  const [telegramPkgs, setTelegramPkgs] = useState([]);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [tgPage, setTgPage] = useState(0);
  const [tgRowsPerPage, setTgRowsPerPage] = useState(10);

  // Shared dialog
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [selectedPkgSource, setSelectedPkgSource] = useState("general");

  const loadPackages = async () => {
    setPackagesLoading(true);
    try {
      const res = await getPackages();
      setPackages(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Failed to load packages", err);
    } finally {
      setPackagesLoading(false);
    }
  };

  const loadTelegramPackages = async () => {
    setTelegramLoading(true);
    try {
      const res = await getAllTelegramPackagesAdmin();
      const data = res.data?.data || res.data || [];
      setTelegramPkgs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load telegram packages", err);
    } finally {
      setTelegramLoading(false);
    }
  };

  useEffect(() => {
    loadPackages();
    loadTelegramPackages();
  }, []);

  const visiblePackages = packages.slice(
    packagesPage * packagesRowsPerPage,
    packagesPage * packagesRowsPerPage + packagesRowsPerPage
  );

  const visibleTelegram = telegramPkgs.slice(
    tgPage * tgRowsPerPage,
    tgPage * tgRowsPerPage + tgRowsPerPage
  );

  function openPkgDialog(pkg, source = "general") {
    setSelectedPkg(pkg);
    setSelectedPkgSource(source);
  }

  return (
    <Box sx={{ p: 3, pt: { xs: 10, sm: 12 } }}>
      {/* ====== General Packages ====== */}
      <Typography variant="h5" gutterBottom>
        Packages
      </Typography>

      <Paper variant="outlined" sx={{ mb: 4, overflow: "hidden" }}>
        {packagesLoading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Hidden</TableCell>
                    <TableCell>Featured</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePackages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        No packages found
                      </TableCell>
                    </TableRow>
                  ) : (
                    visiblePackages.map((pkg, idx) => (
                      <TableRow key={pkg.id || idx}>
                        <TableCell>
                          {packagesPage * packagesRowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell>{pkg.title}</TableCell>
                        <TableCell>{pkg.isHidden ? "Yes" : "No"}</TableCell>
                        <TableCell>{pkg.isFeatured ? "Yes" : "No"}</TableCell>
                        <TableCell>
                          {pkg.createdAt ? new Date(pkg.createdAt).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="primary"
                            onClick={() => openPkgDialog(pkg, "general")}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={packages.length}
              page={packagesPage}
              onPageChange={(_, newPage) => setPackagesPage(newPage)}
              rowsPerPage={packagesRowsPerPage}
              onRowsPerPageChange={(e) => {
                setPackagesRowsPerPage(parseInt(e.target.value, 10));
                setPackagesPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>

      {/* ===== Telegram Packages (now same columns as general) ===== */}
      <Typography variant="h5" gutterBottom>
        Telegram Packages
      </Typography>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        {telegramLoading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Hidden</TableCell>
                    <TableCell>Featured</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleTelegram.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        No telegram packages found
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleTelegram.map((pkg, idx) => (
                      <TableRow key={pkg.id || idx}>
                        <TableCell>
                          {tgPage * tgRowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell>{pkg.title}</TableCell>
                        <TableCell>{pkg.isHidden ? "Yes" : "No"}</TableCell>
                        <TableCell>{pkg.isFeatured ? "Yes" : "No"}</TableCell>
                        <TableCell>
                          {pkg.createdAt ? new Date(pkg.createdAt).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="primary"
                            onClick={() => openPkgDialog(pkg, "telegram")}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={telegramPkgs.length}
              page={tgPage}
              onPageChange={(_, newPage) => setTgPage(newPage)}
              rowsPerPage={tgRowsPerPage}
              onRowsPerPageChange={(e) => {
                setTgRowsPerPage(parseInt(e.target.value, 10));
                setTgPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>

      {/* ===== Shared Details Dialog (both use same notepad rendering) ===== */}
      <Dialog
        open={!!selectedPkg}
        onClose={() => setSelectedPkg(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPkgSource === "telegram"
            ? "Telegram Package Details"
            : "Package Details"}
        </DialogTitle>
        <Divider />
        <DialogContent dividers>
          {selectedPkg ? (
            <Box>
              {selectedPkg.imageUrl && (
                <Box sx={{ mb: 2 }}>
                  <img
                    src={selectedPkg.imageUrl}
                    alt="Package"
                    style={{
                      maxWidth: "100%",
                      borderRadius: 8,
                      boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                    }}
                  />
                </Box>
              )}
              <Typography variant="h6">{selectedPkg.title}</Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  mt: 2,
                  fontFamily: "monospace",
                  background: "#0f172a",
                  color: "#e2e8f0",
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {selectedPkg.details || "No details available"}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setSelectedPkg(null)}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
